// apps/admin/src/components/shell/NotificationBell.tsx
//
// Phase 8.1 Wave 1 Task 03 — TopBar notification bell + dropdown shell.
// Wires useNotificationStore to UI. WS push handling + REST fetch are owned
// by the app boot layer (task-02 / wave 2) — this component only consumes
// store state.: 10-item dropdown, 6 stored categories + 1
// virtual "All" tab, Mark all read, per-item dismiss, 2 sounds (normal vs
// critical), unread badge on bell icon.
//
// vg-binding: CONTEXT:decisions=P8.D-22,P8.D-24,P8.D-27,P8.D-150,P8.D-152,P8.D-161,P8.D-185,P8.D-211,P8.D-212
// vg-binding: UI-SPEC:§5.4
// vg-binding: INTERFACE-STANDARDS:error-shape
// vg-binding: CONTEXT:decisions           (P15.D-21 fail-closed banner + optimistic rollback; Task 25)
//
// Decisions:
//   - badge counts surfaced via useNotificationStore.pendingCountByKey
//   - TopBar 56px height (set by parent TopBar)
//   - 7 filter tabs (6 categories + virtual "All"), 2 sounds
//     real-time WS updates (subscription wired upstream)
//   - @printway/ui primitives
//   - Spinner for in-flight states
//   - WS 4 channels: orders/vendors/support/system
//   - Phase 8 mocks delivery; Phase 9 lands real backend
//   - 3 collections (notifications, announcements, system_events)
//   - WS event schemas

import {
  useEffect,
  useRef,
  useState,
  type JSX,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  ensureNotificationPermission,
  showDesktopNotification,
  useToast,
} from '@printway/ui';

import { useTranslation } from '../../i18n/useTranslation.js';
import { translateNotification } from '../../lib/notificationsI18n.js';
import {
  markAllNotificationsReadOnServer,
  markNotificationReadOnServer,
} from '../../features/notifications/useNotificationFeed.js';
import {
  NOTIFICATION_CATEGORIES,
  useNotificationStore,
  type NotificationFilter,
} from '../../stores/useNotificationStore.js';

type TFn = (key: string, vars?: Record<string, unknown>) => string;

function buildFilterLabels(t: TFn): Record<NotificationFilter, string> {
  return {
    all: t('chrome.notificationBell.filters.all'),
    orders: t('chrome.notificationBell.filters.orders'),
    products: t('chrome.notificationBell.filters.products'),
    stores: t('chrome.notificationBell.filters.stores'),
    //  cross-portal naming: admin shows "Billing" for the
    // same category Vendor Portal calls "Payments". Both are brand/portal
    // terms kept verbatim across locales.
    vendors: 'Vendor',
    billing: 'Billing',
    system: t('chrome.notificationBell.filters.system'),
  };
}

function cn(...classes: Array<string | false | null | undefined>): string {
  return classes.filter(Boolean).join(' ');
}

function formatRelative(iso: string, t: TFn): string {
  const d = new Date(iso).getTime();
  if (Number.isNaN(d)) return '';
  const diff = Date.now() - d;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return t('chrome.notificationBell.timeAgo.justNow');
  if (mins < 60)
    return t('chrome.notificationBell.timeAgo.minutes', { count: mins });
  const hours = Math.floor(mins / 60);
  if (hours < 24)
    return t('chrome.notificationBell.timeAgo.hours', { count: hours });
  const days = Math.floor(hours / 24);
  return t('chrome.notificationBell.timeAgo.days', { count: days });
}

export interface NotificationBellProps {
  /** Optional callback when user clicks "View all" → routes to /notifications. */
  onViewAll?: () => void;
}

export function NotificationBell({
  onViewAll,
}: NotificationBellProps = {}): JSX.Element {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const { t, locale } = useTranslation('admin');
  const FILTER_LABELS = buildFilterLabels(t);

  const navigate = useNavigate();
  const { toast } = useToast();
  const qc = useQueryClient();
  const items = useNotificationStore((s) => s.items);
  const filter = useNotificationStore((s) => s.filter);
  const setFilter = useNotificationStore((s) => s.setFilter);
  const setItems = useNotificationStore((s) => s.setItems);
  const markRead = useNotificationStore((s) => s.markRead);
  const markUnread = useNotificationStore((s) => s.markUnread);
  const markAllRead = useNotificationStore((s) => s.markAllRead);
  const dismiss = useNotificationStore((s) => s.dismiss);
  const consumeSound = useNotificationStore((s) => s.consumeSound);
  const pendingSound = useNotificationStore((s) => s.pendingSound);
  // Fail-closed flag (P15.D-21): 403 AUDIENCE_FORBIDDEN → banner + retry.
  const forbidden = useNotificationStore((s) => s.forbidden);

  const unreadCount = items.filter((n) => !n.isRead).length;
  const visible =
    filter === 'all' ? items : items.filter((n) => n.category === filter);
  const dropdownItems = visible.slice(0, 10);

  // Retry the feed after a 403 AUDIENCE_FORBIDDEN (P15.D-21) — re-runs the
  // fetch; the store flips `forbidden`/`items` from the fresh HTTP status.
  const retryFeed = (): void => {
    void qc.invalidateQueries({ queryKey: ['admin', 'notifications', 'bell'] });
  };

  // Optimistic mark-read + onError ROLLBACK + error toast (P15.D-21). Store
  // update clears the dot instantly; a server failure reverts the row to unread
  // and surfaces the failure instead of silently swallowing it (#2555 kept the
  // read-state on the server, but a rejected write must not lie to the user).
  const markReadSynced = (id: string): void => {
    const wasUnread = items.some((n) => n.id === id && !n.isRead);
    if (!wasUnread) return; // idempotent — nothing to persist / roll back
    markRead(id);
    void markNotificationReadOnServer(id).catch((err: unknown) => {
      markUnread(id);
      toast({
        variant: 'error',
        title: t('chrome.notificationBell.markReadError'),
        description: err instanceof Error ? err.message : undefined,
      });
    });
  };

  const markAllReadSynced = (): void => {
    const snapshot = items;
    markAllRead();
    void markAllNotificationsReadOnServer().catch((err: unknown) => {
      // Roll back to the pre-optimistic read-state so the badge is truthful.
      setItems(snapshot);
      toast({
        variant: 'error',
        title: t('chrome.notificationBell.markAllReadError'),
        description: err instanceof Error ? err.message : undefined,
      });
    });
  };

  // Clicking a row opens its target. Deep-link comes from `link_url` persisted
  // by the notification worker; rows without one just get marked read.
  const openItem = (n: (typeof items)[number]): void => {
    markReadSynced(n.id);
    if (n.href) {
      setOpen(false);
      navigate(n.href);
    }
  };

  // Ask for browser-notification permission once the bell mounts, so enabling
  // it in the OS/browser lets the pushes below actually show (dbg-20260710).
  useEffect(() => {
    void ensureNotificationPermission();
  }, []);

  // ---------------------------------------------------------------------------
  // New-notification attention: SOUND dropped (dbg-20260710) — push a browser
  // desktop notification instead. `pendingSound` is the store's "new item
  // arrived" signal; we consume it and surface the newest item as a native
  // notification (permission-gated, silent no-op otherwise). Clicking it
  // focuses the tab + deep-links via the item's href.
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!pendingSound) return;
    if (typeof window === 'undefined') return;
    if (!consumeSound()) return;
    const latest = items[0];
    if (!latest) return;
    // Same locale re-render as the dropdown rows — the OS toast must not show
    // the dispatch-time language when the admin has switched locale.
    const copy = translateNotification(latest, locale);
    showDesktopNotification(copy.title, {
      body: copy.body,
      tag: `admin-notif-${latest.id}`,
      onClick: latest.href ? () => navigate(latest.href as string) : undefined,
    });
  }, [pendingSound, consumeSound, items, navigate, locale]);

  // Click-outside + Escape close.
  useEffect(() => {
    if (!open) return;
    const onDoc = (ev: MouseEvent): void => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(ev.target as Node)) setOpen(false);
    };
    const onKey = (ev: KeyboardEvent): void => {
      if (ev.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // 7 tabs = "all" + 6 stored categories.
  const filters: NotificationFilter[] = ['all', ...NOTIFICATION_CATEGORIES];

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        aria-label={t('chrome.notificationBell.title')}
        aria-expanded={open}
        data-testid="notification-bell-trigger"
        onClick={() => setOpen((o) => !o)}
        className="relative inline-flex h-9 w-9 items-center justify-center rounded-md text-pw-text transition-colors hover:bg-pw-surface focus:outline-none focus:ring-2 focus:ring-pw-primary-strong/40"
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          className="h-5 w-5"
          aria-hidden="true"
        >
          <path d="M15 17h5l-1.4-1.4A2 2 0 0 1 18 14V11a6 6 0 1 0-12 0v3a2 2 0 0 1-.6 1.4L4 17h5" />
          <path d="M9 17a3 3 0 0 0 6 0" />
        </svg>
        {unreadCount > 0 && (
          <span
            data-testid="notification-bell-badge"
            className="absolute -right-1 -top-1 inline-flex min-w-[18px] items-center justify-center rounded-full bg-pw-primary-strong px-1 text-[10px] font-semibold leading-4 text-white"
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div
          role="dialog"
          aria-label={t('chrome.notificationBell.title')}
          className="absolute right-0 z-50 mt-2 w-[26rem] max-w-[calc(100vw-1rem)] overflow-hidden rounded-lg border border-pw-border bg-pw-bg shadow-lg"
        >
          {/* Header */}
          <div className="flex items-center justify-between border-b border-pw-border px-4 py-2">
            <span className="text-sm font-semibold text-pw-text">{t('chrome.notificationBell.title')}</span>
            <button
              type="button"
              onClick={markAllReadSynced}
              disabled={unreadCount === 0}
              data-testid="notification-mark-all-read"
              className="text-xs font-medium text-pw-primary-strong transition-colors hover:underline disabled:cursor-not-allowed disabled:opacity-50"
            >
              {t('chrome.notificationBell.markAllRead')}
            </button>
          </div>

          {/* Filter tabs — 7 tabs */}
          <div
            role="tablist"
            className="flex overflow-x-auto border-b border-pw-border px-1"
          >
            {filters.map((f) => (
              <button
                key={f}
                type="button"
                role="tab"
                aria-selected={filter === f}
                data-testid={`notification-filter-${f}`}
                onClick={() => setFilter(f)}
                className={cn(
                  'shrink-0 border-b-2 px-3 py-2 text-xs font-medium transition-colors',
                  filter === f
                    ? 'border-pw-primary-strong text-pw-primary-strong'
                    : 'border-transparent text-pw-text-muted hover:text-pw-text',
                )}
              >
                {FILTER_LABELS[f]}
              </button>
            ))}
          </div>

          {/* List */}
          <div className="max-h-96 overflow-y-auto">
            {forbidden ? (
              // 2-case revoke (P15.D-21): 403 AUDIENCE_FORBIDDEN → banner +
              // retry, NOT a silent empty state.
              <div
                role="alert"
                data-testid="notif-forbidden-banner"
                className="flex flex-col items-center gap-2 px-4 py-8 text-center"
              >
                <p className="text-sm font-medium text-pw-text">
                  {t('chrome.notificationBell.forbidden.title')}
                </p>
                <p className="text-xs text-pw-text-muted">
                  {t('chrome.notificationBell.forbidden.body')}
                </p>
                <button
                  type="button"
                  data-testid="notif-retry-btn"
                  onClick={retryFeed}
                  className="mt-1 rounded-md border border-pw-border px-3 py-1 text-xs font-medium text-pw-primary-strong transition-colors hover:bg-pw-surface"
                >
                  {t('chrome.notificationBell.forbidden.retry')}
                </button>
              </div>
            ) : dropdownItems.length === 0 ? (
              <p className="px-4 py-8 text-center text-sm text-pw-text-muted">
                {t('chrome.notificationBell.empty')}
              </p>
            ) : (
              dropdownItems.map((n) => {
                // Re-render at the ACTIVE locale from `i18nKey`; the stored
                // title/body (dispatch-time language) is only the fallback,
                // else a locale switch leaves rows frozen.
                const copy = translateNotification(n, locale);
                return (
                <div
                  key={n.id}
                  data-testid={`notification-item-${n.id}`}
                  className={cn(
                    'flex items-start gap-3 border-b border-pw-border px-4 py-3 transition-colors',
                    n.isRead ? 'bg-transparent' : 'bg-pw-surface/50',
                  )}
                >
                  <button
                    type="button"
                    data-testid="notif-markread-btn"
                    onClick={() => markReadSynced(n.id)}
                    aria-label={t('shellChrome.markAsReadAria')}
                    className="mt-1 inline-flex h-2.5 w-2.5 shrink-0 items-center justify-center rounded-full"
                  >
                    <span
                      className={cn(
                        'block h-2.5 w-2.5 rounded-full',
                        n.isRead
                          ? 'bg-transparent'
                          : n.severity === 'critical'
                            ? 'bg-red-500'
                            : 'bg-pw-primary-strong',
                      )}
                    />
                  </button>
                  {/* The WHOLE row opens the target, not just the small CTA
                      below it. Pre-fix (dbg-20260729-065255-9904) `openItem`
                      was wired only to the "View detail" link, so clicking the
                      notification title/body — the obvious target — did
                      nothing at all. Rows with no `href` stay a plain <div> so
                      we never render a button that cannot act. */}
                  <div
                    {...(n.href
                      ? {
                          role: 'button' as const,
                          tabIndex: 0,
                          onClick: () => openItem(n),
                          onKeyDown: (ev: ReactKeyboardEvent) => {
                            if (ev.key === 'Enter' || ev.key === ' ') {
                              ev.preventDefault();
                              openItem(n);
                            }
                          },
                          'aria-label': t('chrome.notificationBell.viewDetail'),
                        }
                      : {})}
                    className={cn(
                      'min-w-0 flex-1 text-left',
                      n.href
                        ? 'cursor-pointer rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-pw-primary-strong/40'
                        : '',
                    )}
                  >
                    <p className="truncate text-sm font-medium text-pw-text">
                      {copy.title}
                    </p>
                    <p className="line-clamp-2 text-xs text-pw-text-muted">
                      {copy.body}
                    </p>
                    <p className="mt-1 text-[10px] uppercase tracking-wide text-pw-text-muted">
                      {FILTER_LABELS[n.category]} · {formatRelative(n.createdAt, t)}
                    </p>
                    {/* Deep-link CTA — #2555 B3 requires the bell to open the
                        exact ticket, not the list. `href` is the worker-
                        persisted `link_url`. Kept as an explicit affordance on
                        top of the row-wide click above. */}
                    {n.href ? (
                      <span
                        data-testid={`notification-view-detail-${n.id}`}
                        className="mt-1 inline-block text-xs font-medium text-pw-primary-strong transition-colors group-hover:underline"
                      >
                        {t('chrome.notificationBell.viewDetail')}
                      </span>
                    ) : null}
                  </div>
                  <button
                    type="button"
                    onClick={() => dismiss(n.id)}
                    aria-label={t('shellChrome.dismissAria')}
                    className="ml-2 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded text-pw-text-muted transition-colors hover:bg-pw-surface hover:text-pw-text"
                  >
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="h-3.5 w-3.5"
                      aria-hidden="true"
                    >
                      <line x1="18" y1="6" x2="6" y2="18" />
                      <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  </button>
                </div>
                );
              })
            )}
          </div>

          {/* Footer: View all */}
          <div className="border-t border-pw-border px-4 py-2 text-center">
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                onViewAll?.();
              }}
              className="text-xs font-medium text-pw-primary-strong transition-colors hover:underline"
            >
              {t('chrome.notificationBell.viewAll')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
